import { useQuery } from '@tanstack/react-query';
import { Angry, Flame, Hourglass } from 'lucide-react';
import { useMemo } from 'react';
import { get } from '../lib/api';
import { Badge } from './ui';

/** Análise da IA de um atendimento (urgência e humor do cliente). */
export interface InsightView {
  attendanceId: string;
  urgency: 'baixa' | 'media' | 'alta';
  mood: 'calmo' | 'neutro' | 'impaciente' | 'irritado';
  reason: string | null;
  analyzedAt: string;
  /** A IA viu o cliente irritado em algum momento (atendimento já encerrado). */
  everIrritated?: boolean;
}

/** Análise de um atendimento em andamento. */
export interface ActiveInsight extends InsightView {
  status: string;
  operatorId: string | null;
  /** Só para quem vê relatórios. */
  operator: string | null;
  contact: string | null;
}

/** Análises atuais (lista pequena: só atendimentos em andamento já analisados). Relida pelo aviso `insight.updated`. */
export function useActiveInsights() {
  const q = useQuery({ queryKey: ['insights', 'active'], queryFn: () => get<ActiveInsight[]>('/insights/active'), refetchInterval: 60_000 });
  return useMemo(() => new Map((q.data ?? []).map((i) => [i.attendanceId, i])), [q.data]);
}

/** Última análise de atendimentos específicos, inclusive encerrados. */
export function useInsightsFor(ids: string[]) {
  const key = [...ids].sort().join(',');
  const q = useQuery({
    queryKey: ['insights', 'attendances', key],
    queryFn: () => get<InsightView[]>(`/insights/attendances?ids=${key}`),
    enabled: ids.length > 0 && ids.length <= 200,
    staleTime: 60_000,
  });
  return useMemo(() => new Map((q.data ?? []).map((i) => [i.attendanceId, i])), [q.data]);
}

/** Selos de urgência e humor (só o que chama atenção: urgência alta/média, cliente impaciente/irritado). */
export function InsightBadges({ insight }: { insight: InsightView | undefined }) {
  if (!insight) return null;
  const title = `Análise da IA${insight.reason ? `: ${insight.reason}` : ''}`;
  return (
    <>
      {insight.urgency === 'alta' && (
        <span title={title}>
          <Badge tone="danger">
            <Flame className="size-3" /> urgente
          </Badge>
        </span>
      )}
      {insight.urgency === 'media' && (
        <span title={title}>
          <Badge tone="warn">urgência média</Badge>
        </span>
      )}
      {(insight.mood === 'irritado' || insight.everIrritated) && (
        <span title={title}>
          <Badge tone="danger">
            <Angry className="size-3" /> {insight.mood === 'irritado' ? 'irritado' : 'ficou irritado'}
          </Badge>
        </span>
      )}
      {insight.mood === 'impaciente' && !insight.everIrritated && (
        <span title={title}>
          <Badge tone="warn">
            <Hourglass className="size-3" /> impaciente
          </Badge>
        </span>
      )}
    </>
  );
}
